import React, { useEffect, useRef } from 'react';
import { ChevronDown, Compass, Activity, Radio } from 'lucide-react';
import { ChapterSummary, PageData } from '../../types/tome';
import { VisualEntry } from '../../services/visualRegistry';

interface AnimatedChapterSplashProps {
  chapter: ChapterSummary;
  page: PageData;
  visual?: VisualEntry;
  onScrollIntoChapter?: () => void;
}

// In-Universe Chapter Epigraphs & Philosophy of the Caesura Engine
const CHAPTER_EPIGRAPHS: Record<number, { quote: string; attribution: string }> = {
  0: {
    quote: "A second is not a smooth glass slide. It is sixty frames of meat and copper, stitched together across an abyss no one was ever meant to name.",
    attribution: "Navigation Guild Technical Doctrine, Rev. 44-B"
  },
  1: {
    quote: "Between the tick and the tock, the universe exhales. If you are fast enough, you can slip your fingers into the lung.",
    attribution: "Marcus Ward, Personal Maintenance Journal (Pre-Shear)"
  },
  2: {
    quote: "Copper smells sweet when it is melting in zero atmosphere. It smells like someone's childhood burning inside a lead can.",
    attribution: "USSC Geryon Flight Recorder // Keel Segment 4"
  },
  3: {
    quote: "Do not look at the unrendered stars. The retina remembers coordinates that the occipital lobe cannot compile.",
    attribution: "Dr. Simon Peter, Chrono-Psychiatric Advisory"
  },
  4: {
    quote: "The hull did not groan because of stress. It was the sound of iron atoms realizing they had missed three consecutive milliseconds of reality.",
    attribution: "Chief Architect Elena Cruz // Acoustic Telemetry"
  },
  5: {
    quote: "The Great Null Rift is not empty. It is simply unindexed in the municipal memory.",
    attribution: "Combine Cartographic Inquest #109"
  },
  6: {
    quote: "Three seconds in the pause is twenty years in the marrow. Keep your helmet sealed.",
    attribution: "Cadet Silas Vance, Comms Intercept"
  },
  7: {
    quote: "There is a room behind the bulkheads that has no square footage on the blueprints. The lights are always on.",
    attribution: "Geryon Night-Watch Log"
  },
  8: {
    quote: "Time dies from the edges inward, like frost on an unheated windshield.",
    attribution: "Tribunal Record // Case Ward-77"
  },
  9: {
    quote: "When core containment fails, time does not explode outward. It collapses like a lung pierced with cold wire.",
    attribution: "Engineering Emergency Log 0x09"
  },
  10: {
    quote: "Walking the spine without sequence is like swimming through broken mirror shards. Every step cuts a different year.",
    attribution: "Marcus Ward, Audio Fragment"
  },
  11: {
    quote: "In the absolute pause, you can hear the stars breathing. It is not a gentle sound.",
    attribution: "The Obsidian Ledger // Marginalia Entry 1"
  },
  12: {
    quote: "Frozen photons are heavy as buckshot. They will tear through your visor if you move too fast.",
    attribution: "Elena Cruz, Jump Notes"
  },
  13: {
    quote: "They do not have eyes. They have geometries. They do not watch you; they calculate what you displace.",
    attribution: "The Spectator Archive // Encounter 04"
  },
  14: {
    quote: "What was omitted from the ledger cannot be remembered, only grieved.",
    attribution: "USSC Geryon Memorial Roster"
  },
  15: {
    quote: "Elena spoke to the gap. The gap did not answer with words, but with the coordinates of where her children will die.",
    attribution: "Dr. Simon Peter, Observation Note"
  },
  16: {
    quote: "The observer tax is paid in myelin sheath and short-term memory.",
    attribution: "DSM-Chrono VII Diagnostic Manual"
  },
  17: {
    quote: "I remember tomorrow's breakfast. It tastes of cold powdered egg and my own blood.",
    attribution: "Marcus Ward, Psych Evaluation"
  },
  18: {
    quote: "The grammar began to decay around noon. By supper, nouns had weight and verbs were treason.",
    attribution: "Mess Deck Intercom Transcript"
  },
  19: {
    quote: "Subject exhibits 40Hz ocular micro-saccades corresponding to the clock cycle of the core coils.",
    attribution: "Dr. Simon Peter, Clinical Debrief"
  },
  20: {
    quote: "My body is no longer a man. It is a postal address for a delivery that was canceled twenty years ago.",
    attribution: "Marcus Ward, Transmit Log"
  },
  21: {
    quote: "Every atom is a ledger balance. When you rewrite the ledger, the universe must balance the debit in bone.",
    attribution: "The Obsidian Ledger // Lemma VI"
  },
  22: {
    quote: "Three metric kilotons of iron are missing from the hull. The ship did not lose them; they were never entered.",
    attribution: "Quartermaster Logistics Audit"
  },
  23: {
    quote: "Calculus is easy when time flows. When time is a frozen monolith, mathematics is surgery without anesthesia.",
    attribution: "Elena Cruz // Final Calculus"
  },
  24: {
    quote: "The garbage collector does not delete. It merely unmaps the pointers. You remain, floating and unaddressed.",
    attribution: "Kernel Level Zero Specification"
  },
  25: {
    quote: "Strip the keel down to the titanium ribs. If we carry mass into the seam, we will drag the world in with us.",
    attribution: "Marcus Ward, EVA Intercom"
  },
  30: {
    quote: "Strike the flint against the vacuum. Light the next second before the dark catches our scent.",
    attribution: "Elena Cruz, Command Override"
  },
};

export const AnimatedChapterSplash: React.FC<AnimatedChapterSplashProps> = ({
  chapter,
  page,
  visual,
  onScrollIntoChapter,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Micro-animated canvas shader: Planck coordinate grid + particle dust
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', handleResize);

    // Particle nodes floating across the coordinate grid
    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      alpha: number;
      color: string;
    }> = [];

    const colors = ['#00f3ff', '#00e5ff', '#ffb700', '#7c4dff', '#ffffff'];
    for (let i = 0; i < 45; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: -0.2 - Math.random() * 0.5,
        size: Math.random() * 2 + 0.8,
        alpha: Math.random() * 0.6 + 0.2,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    let frame = 0;
    const render = () => {
      frame++;
      ctx.clearRect(0, 0, width, height);

      // Deep dark cosmic background with subtle radial gradient
      const grad = ctx.createRadialGradient(
        width / 2,
        height * 0.4,
        50,
        width / 2,
        height * 0.5,
        Math.max(width, height)
      );
      grad.addColorStop(0, 'rgba(6, 14, 28, 0.7)');
      grad.addColorStop(0.6, 'rgba(3, 6, 12, 0.95)');
      grad.addColorStop(1, '#020408');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Procedural Planck coordinate grid lines
      ctx.lineWidth = 1;
      const gridSize = 48;
      const gridOffset = (frame * 0.2) % gridSize;

      ctx.strokeStyle = 'rgba(0, 243, 255, 0.04)';
      for (let x = 0; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      for (let y = gridOffset; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      // Horizontal scanning laser line
      const scanY = (frame * 1.5) % height;
      const scanGrad = ctx.createLinearGradient(0, scanY - 30, 0, scanY + 30);
      scanGrad.addColorStop(0, 'rgba(0, 243, 255, 0)');
      scanGrad.addColorStop(0.5, 'rgba(0, 243, 255, 0.12)');
      scanGrad.addColorStop(1, 'rgba(0, 243, 255, 0)');
      ctx.fillStyle = scanGrad;
      ctx.fillRect(0, scanY - 30, width, 60);

      // Render floating Planck dust particles
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < 0) p.y = height;
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;

        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha * (0.6 + 0.4 * Math.sin(frame * 0.03 + p.x));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Subtle crosshair around select particles
        if (p.size > 2.2) {
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(p.x - 4, p.y);
          ctx.lineTo(p.x + 4, p.y);
          ctx.moveTo(p.x, p.y - 4);
          ctx.lineTo(p.x, p.y + 4);
          ctx.stroke();
        }
      }
      ctx.globalAlpha = 1.0;

      animId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const epigraph =
    CHAPTER_EPIGRAPHS[chapter.chapterNumber] ||
    CHAPTER_EPIGRAPHS[1] || {
      quote: "Between the tick and the tock, the universe exhales.",
      attribution: "The Caesura Archive"
    };

  return (
    <div className="animated-chapter-splash-root" data-chapter-number={chapter.chapterNumber}>
      {/* Real-Time Shader Canvas Background */}
      <canvas ref={canvasRef} className="chapter-splash-canvas" />

      {/* Atmospheric Vignette & Scanlines */}
      <div className="chapter-splash-scanlines" />
      <div className="chapter-splash-vignette" />

      {/* Central Monumental Typography Container */}
      <div className="chapter-splash-content">
        {/* Act Telemetry Banner */}
        <div className="chapter-act-herald">
          <span className="chapter-live-dot" />
          <span className="chapter-act-name">
            {chapter.actNumber === 0
              ? 'PROLOGUE // PRE-FLIGHT AUTHENTICATION'
              : chapter.actNumber === 11
              ? 'EPILOGUE // CODEX ARCHIVES'
              : `ACT ${chapter.actNumber}: ${page.actTitle.toUpperCase()}`}
          </span>
          <span className="chapter-coord-tag">
            CHRONO-INTERVAL {chapter.startPage}—{chapter.endPage}
            {visual ? ` // ${visual.title.toUpperCase()}` : ''}
          </span>
        </div>

        {/* Monumental Chapter Number Marker */}
        <div className="chapter-numeral-wrapper">
          <div className="chapter-numeral-box">
            <span className="numeral-label">
              {chapter.chapterNumber === 0 ? 'ENTRY // 00' : `CHAPTER ${chapter.chapterNumber}`}
            </span>
          </div>
          <span className="numeral-archetype-badge">
            // {chapter.contentArchetype?.toUpperCase() || 'NARRATIVE DISPATCH'}
          </span>
        </div>

        {/* Chapter Title Headline */}
        <h1 className="chapter-monumental-title">
          {chapter.title.replace(/^Chapter \d+:\s*/i, '')}
        </h1>

        {/* Philosophical Chapter Epigraph Card */}
        <div className="chapter-epigraph-card">
          <div className="epigraph-quote-mark">“</div>
          <blockquote className="epigraph-quote-text">{epigraph.quote}</blockquote>
          <cite className="epigraph-attribution">— {epigraph.attribution}</cite>
        </div>

        {/* Telemetry Status Grid */}
        <div className="chapter-telemetry-row">
          <div className="telemetry-item">
            <Activity size={13} className="telemetry-icon" />
            <span className="telemetry-label">TEMPORAL SHIFT:</span>
            <span className="telemetry-val">{page.shader.theme.toUpperCase()}</span>
          </div>
          <div className="telemetry-divider">//</div>
          <div className="telemetry-item">
            <Radio size={13} className="telemetry-icon" />
            <span className="telemetry-label">SUBHARMONIC MOOD:</span>
            <span className="telemetry-val">{page.audio.mood.toUpperCase()} (40Hz)</span>
          </div>
          <div className="telemetry-divider">//</div>
          <div className="telemetry-item">
            <Compass size={13} className="telemetry-icon" />
            <span className="telemetry-label">PLANCK TICK:</span>
            <span className="telemetry-val">{page.timestamp || '00:00:00.00'}</span>
          </div>
        </div>

        {/* Scroll invitation button */}
        <div
          className="chapter-scroll-invitation"
          onClick={onScrollIntoChapter}
          title="Scroll down to begin narrative transmission"
        >
          <span className="invitation-text">PROCEED INTO CHAPTER TRANSMISSION</span>
          <div className="invitation-arrow-bounce">
            <ChevronDown size={18} />
          </div>
        </div>
      </div>
    </div>
  );
};
